import { MotionSource, MotionSourceType } from '../motion/MotionSource';
import { getAudioContext } from './Context';
import { AUDIO_SOURCE_IDS } from './AudioSource';
import { trimBuffer, normalizeBuffer, getAudioBuffer } from './AudioUtils';
import Storable from '../data/Storable';

import {
  createAnalyzer,
  sampleBuffer,
  detectBeat,
  beatVolumes
} from '@beatflyer/audio-lib';

// import __debug from './debug'
import { loadFile, newUUID } from '../utils/index';
import { reportIssue } from '../utils/Tracker';
import { SignalSource } from '../data/SignalSource';
import Beat from '@beatflyer/audio-lib/types/analyzers/beat/Beat';
import Analyzer from '@beatflyer/audio-lib/types/analyzers/Analyzer';

export class Soundtrack extends MotionSource implements Storable {
  id: string = newUUID();
  file: File | null = null;
  offset: number = 0; // millis
  url: string;
  thumbUrl: string | null = null;
  title: string | null = null;
  artist: string | null = null;

  needsTrimming: boolean;
  audio: AudioBufferSourceNode | null;
  audioBuffer: AudioBuffer;
  // audioFadeLength = 0;

  private _volume = 1;
  private _muted = false;
  private _lastTime = 0;
  private audioContext: AudioContext;
  private gainNode: GainNode;
  private frequencyData: Uint8Array;
  private waveformData: Uint8Array;
  private startTime: number;
  private analyser: AnalyserNode;
  private stream: any;
  private analyzers: Analyzer[];

  constructor(needsTrimming: boolean) {
    super();
    this.hasAudio = true;
    this.needsTrimming = needsTrimming;

    this.audioContext = getAudioContext();
    this.gainNode = this.audioContext.createGain();
    this.gainNode.connect(this.audioContext.destination);
    this.type = MotionSourceType.Soundtrack;
  }

  get volume(): number {
    return this._volume;
  }

  set volume(v: number) {
    this._volume = v;
    if (!this.muted) {
      this.gainNode.gain.setValueAtTime(v, this.audioContext.currentTime);
    }
  }

  get muted(): boolean {
    return this._muted;
  }

  set muted(v: boolean) {
    if (v === this._muted) {
      return;
    }
    this._muted = v;
    this.gainNode.gain.setValueAtTime(
      v ? 0 : this._volume,
      this.audioContext.currentTime
    );
  }

  isCustom(): boolean {
    return true;
  }

  getAudioStream(): any {
    return this.stream;
  }

  async load(urlOrFile: string | File) {
    let audioFile: File;
    if (typeof urlOrFile === 'string') {
      this.url = urlOrFile as string;
      const blob = await loadFile(this.url);
      audioFile = new File([blob], this.url.split('/').pop() as string);
    } else {
      this.file = urlOrFile as File;
      audioFile = this.file;
    }
    const buffer = await getAudioBuffer(audioFile);
    return this.audioDidLoad(buffer);
  }

  loadAudio(url: string): Promise<AudioBuffer> {
    return new Promise((resolve, reject) => {
      var request = new XMLHttpRequest();
      request.open('GET', url, true);
      request.responseType = 'arraybuffer';
      request.onload = () => {
        var audioData = request.response;
        this.audioContext.decodeAudioData(audioData, resolve, reject);
      };
      request.send();
    });
  }

  async audioDidLoad(buffer: AudioBuffer) {
    this.audioBuffer = this.needsTrimming ? trimBuffer(buffer) : buffer;
    this.audioBuffer = normalizeBuffer(this.audioBuffer);
    this.analyzers = [];
    const samples: Uint8Array[] = await sampleBuffer(this.audioBuffer);
    const beat: Beat = await detectBeat(this.audioBuffer);
    this.bpm = beat.tempo;
    console.log('Beat detaction...');
    console.log(beat);
    const volumes = beatVolumes(buffer, beat);
    AUDIO_SOURCE_IDS.forEach(id => {
      const analyzer = createAnalyzer(id);
      if (analyzer) {
        if (analyzer.isBeat) {
          analyzer.processBeat(
            beat,
            this.audioBuffer.duration,
            samples.length,
            volumes
          );
        } else {
          analyzer.process(samples);
        }
        this.analyzers.push(analyzer);
      } else {
        reportIssue('Could not create analyzer with id: ' + id);
      }
    });
  }
  reset() {
    this.endCallback = null;
    console.log('Prepare Audio');
    if (this.audio) {
      console.log('Audio already prepared');
      return;
    }
    var soundSource = this.audioContext.createBufferSource();
    soundSource.buffer = this.audioBuffer;
    soundSource.onended = e => {
      if (e.target !== this.audio) {
        console.log('Ignore end');
        return;
      }
      if (this.isPlaying) {
        if (this.endCallback) {
          this.endCallback();
          this.endCallback = null;
        } else {
          this.doLoop();
        }
      }
    };
    soundSource.connect(this.gainNode);
    this.startTime = Date.now();

    var destination = (this.audioContext as any).createMediaStreamDestination();
    soundSource.connect(destination);

    this.analyser = this.audioContext.createAnalyser();

    soundSource.connect(this.analyser);
    // soundSource.connect(audioContext.destination);
    if (this.analyser.frequencyBinCount !== 1024) {
      console.warn(
        'analizer is not 1024 long: ' + this.analyser.frequencyBinCount
      );
    }
    this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
    // const frequenciesPerBar = this.frequencyData.length / this.barsCount;
    // if( frequenciesPerBar !== Math.round( frequenciesPerBar ) ){
    //   alert('Can\'t divide frequencies in bars. ' + this.frequencyData.length + ' not multiple of ' + this.barsCount );
    // }
    this.waveformData = new Uint8Array(this.analyser.frequencyBinCount);
    this.audio = soundSource;
    this.stream = destination.stream;
  }
  doLoop() {
    console.log('LOOP');
    this.pause();
    this.offset = 0;
    this.play();
    this.emit('loop');
  }
  play() {
    if (this.isPlaying) {
      return;
    }
    console.log('Soundtrack play');
    if (!this.audio) {
      this.reset();
    }
    this.audio!.start(0, this.offset * 0.001);
    this.isPlaying = true;
    this.startTime = Date.now();
  }
  pause() {
    if (!this.isPlaying) {
      return;
    }
    console.log('Soundtrack pause');
    this._lastTime = this.getCurrentTime();
    this.isPlaying = false;
    this.dispose();
    this.offset = 0;
  }
  resume() {
    if (this.isPlaying) {
      return;
    }
    this.offset = this._lastTime;
    this.play();
  }
  jumpTo(millis: number) {
    // console.log('soundtrack jump to: ' + position);
    this.pause();
    this.offset = millis;
    this.play();
  }
  getCurrentTime() {
    const duration = this.getDuration();
    if (!this.isPlaying) {
      return this._lastTime;
    }
    // console.log('offset: ' + this.offset);
    let elapsed = Date.now() - this.startTime + this.offset;
    while (elapsed > duration) {
      elapsed -= duration;
    }
    return elapsed;
  }

  getLevel(signal: SignalSource = SignalSource.Default) {
    // console.log('frequence: ' + frequence);
    return this.getLevelAt(this.getCurrentTime(), signal);
  }
  // position between 0-1
  getLevelAt(
    millis: number,
    signal: SignalSource = SignalSource.Default
  ): number {
    if (!this.analyzers) {
      return 0;
    }
    if (signal === SignalSource.Default) {
      signal = SignalSource.BeatSmooth;
    }
    const analyzerIndex = AUDIO_SOURCE_IDS.indexOf(signal);
    const analyzer = this.analyzers[analyzerIndex];
    if (!analyzer) {
      console.error(`Could not find analyzer with id ${signal}`);
    }
    const position = millis / this.getDuration();
    return analyzer.getValueAt(position);
  }
  getSpectrum() {
    // TODO: read waveform from stream
    // const res = [];
    // while(res.length<1024){
    //   res.push( Math.floor(Math.random()*255));
    // }
    return this.frequencyData;
  }
  getWaveform() {
    return this.waveformData;
  }
  update() {
    // console.log('UPDATE: ' + this.audioBuffer + ' - ' + this.analyser);
    if (!this.audioBuffer || !this.analyser) {
      return;
    }

    // console.log( this.audioContext.currentTime );
    // update data in frequencyData
    this.analyser.getByteFrequencyData(this.frequencyData);
    this.analyser.getByteTimeDomainData(this.waveformData);
  }
  getDuration(): number {
    if (this.audioBuffer) {
      return this.audioBuffer.duration * 1000;
    }
    return 0;
  }
  dispose() {
    if (this.audio) {
      this.audio.onended = null;
      this.audio.disconnect();
      this.audio.stop(0);
      this.audio = null;
    }
  }
}

export const createSoundtrack = (src: string | File): Promise<Soundtrack> => {
  return new Promise(resolve => {
    const soundtrack = new Soundtrack(false);
    soundtrack.load(src).then(() => {
      resolve(soundtrack);
    });
  });
};



// WEBPACK FOOTER //
// ./src/audio/Soundtrack.ts